import { bidangLabelFromSamplingPlan, type StrapiSampleInformationRow, type StrapiSurveyGridRow } from '../../../api/strapiCrm';
import type { DynamicEntityRow } from '../../../api/strapiDynamicEntity';
import {
  letterCountBySamplingPlanFromRows,
  pickCanonicalSamplingPlans,
} from '../../../sampling/samplingPlanCanonical';

export const MAX_ADDED_SAMPLING_POINTS = 50;

export type SurveyTambahTitikOption = {
  sampling_plan_document_id: string;
  bidang_pengujian_label: string;
  current_point_count: number;
};

export function parseAddedPointCount(raw: string): number | null {
  const n = Number(String(raw).trim());
  if (!Number.isFinite(n) || n < 1) return null;
  const floor = Math.floor(n);
  if (floor > MAX_ADDED_SAMPLING_POINTS) return null;
  return floor;
}

export function tambahTitikOptionLabel(option: SurveyTambahTitikOption): string {
  return `${option.bidang_pengujian_label} — ${option.current_point_count} titik`;
}

export function tambahTitikOptionsFromSurveyRows(rows: StrapiSurveyGridRow[]): SurveyTambahTitikOption[] {
  const byPlan = new Map<string, SurveyTambahTitikOption>();
  for (const row of rows) {
    const id = String(row.sampling_plan_document_id ?? '').trim();
    if (!id) continue;
    const existing = byPlan.get(id);
    const label = row.bidang_pengujian_label?.trim() || 'Bidang pengujian';
    byPlan.set(id, {
      sampling_plan_document_id: id,
      bidang_pengujian_label: existing?.bidang_pengujian_label || label,
      current_point_count: (existing?.current_point_count ?? 0) + 1,
    });
  }
  return [...byPlan.values()].sort((a, b) =>
    a.bidang_pengujian_label.localeCompare(b.bidang_pengujian_label, 'id'),
  );
}

export function tambahTitikOptionsFromPlans(
  plans: DynamicEntityRow[],
  sampleLines: StrapiSampleInformationRow[] = [],
  surveyRows: StrapiSurveyGridRow[] = [],
): SurveyTambahTitikOption[] {
  const letterCounts = letterCountBySamplingPlanFromRows(surveyRows);
  const canonical = pickCanonicalSamplingPlans(plans, letterCounts);
  const options: SurveyTambahTitikOption[] = [];
  for (const plan of canonical) {
    const id = String(plan.documentId ?? '').trim();
    if (!id) continue;
    const label = bidangLabelFromSamplingPlan(plan, plans, sampleLines).trim() || 'Bidang pengujian';
    const count = Math.max(1, Math.floor(Number(plan.sample_point_count) || 1));
    options.push({
      sampling_plan_document_id: id,
      bidang_pengujian_label: label,
      current_point_count: count,
    });
  }
  return options.sort((a, b) => a.bidang_pengujian_label.localeCompare(b.bidang_pengujian_label, 'id'));
}
